import { Edit, Visibility } from '@mui/icons-material'
import { IconButton, Switch, Tooltip } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { format } from 'date-fns'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { features } from '../core/config/features'
import { useWorkouts } from './workouts'
import { useWorkoutSettings } from './workout-settings'
import { useUsers } from './users'

export type UserRole = 'admin' | 'coach'

export const useWorkoutTableColumns = () => {
  const navigate = useNavigate()
  const handleViewClick = useCallback(
    (id: string) => {
      navigate(`/workouts/${id}`)
    },
    [navigate]
  )
  const handleEditClick = useCallback(
    (id: string) => {
      navigate(`/workout/${id}`)
    },
    [navigate]
  )
  const columns: GridColDef[] = useMemo(() => {
    const columns: GridColDef[] = [
      {
        field: 'edit',
        headerName: '',
        width: 10,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,

        renderCell: (params: GridRenderCellParams) => {
          const id = params.value as string
          return (
            <Tooltip title="Redigera pass">
              <IconButton onClick={() => handleEditClick(id)} aria-label="edit">
                <Edit />
              </IconButton>
            </Tooltip>
          )
        },
      },
      {
        field: 'view',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        headerName: '',
        width: 10,
        renderCell: (params: GridRenderCellParams) => {
          const id = params.value as string
          return (
            <Tooltip title="Visa pass">
              <IconButton onClick={() => handleViewClick(id)} aria-label="edit">
                <Visibility />
              </IconButton>
            </Tooltip>
          )
        },
      },
      { field: 'id', headerName: 'ID', hide: true },

      { field: 'type', headerName: 'Typ', minWidth: 150 },
      { field: 'location', headerName: 'Plats' },

      { field: 'startTime', headerName: 'Starttid' },
      { field: 'endTime', headerName: 'Sluttid' },
      {
        field: 'date',
        type: 'date',
        headerName: 'Datum',
      },
      {
        field: 'credits',
        type: 'number',
        headerName: 'Credits',
      },
      {
        field: 'collectionTime',
        headerName: 'Samlingstid',
      },
      {
        field: 'registrationTime',
        headerName: 'Sista registrering',
      },
      { field: 'gear', headerName: 'Utrustning' },
      {
        field: 'numberOfPlaces',
        type: 'number',
        headerName: 'Platser',
      },
      {
        field: 'participantsCount',
        headerName: 'Antal Deltagare',
        minWidth: 150,
        type: 'number',
      },
      {
        field: 'participantsList',
        headerName: 'Deltagare',
        hide: true,
        minWidth: 200,
      },
      {
        field: 'coaches',
        headerName: 'Tränare',
      },
      { field: 'createdAt', headerName: 'Skapad', minWidth: 150 },
      { field: 'updatedAt', headerName: 'Uppdaterad', minWidth: 150 },
    ]

    return features.credits
      ? columns
      : columns.filter((column) => column.field !== 'credits')
  }, [handleEditClick, handleViewClick])
  return columns
}

export const useWorkoutTableRows = () => {
  const { workouts, loading, error } = useWorkouts()
  const { getWorkoutTypeName } = useWorkoutSettings()

  const rows = useMemo(
    () =>
      workouts.map((workout) => {
        return {
          edit: workout.id,
          view: workout.id,
          id: workout.id,
          type: getWorkoutTypeName(workout.type),
          location: workout.location,
          date: format(workout.date.toDate(), 'dd-MMM-yy'),
          startTime: format(workout.startTime.toDate(), 'HH:mm'),
          endTime: format(workout.endTime.toDate(), 'HH:mm'),
          credits: workout.credits,
          collectionTime: workout.collectionTime + ' min',
          registrationTime: workout.registrationTime + 'h',
          gear: workout.gear.join(', '),
          numberOfPlaces: workout.numberOfPlaces,
          participantsCount: workout.participantsCount,
          participantsList: workout.participantsList
            .map((participant) => participant.displayName)
            .join(', '),
          coaches: workout.coaches.map((coach) => coach.displayName).join(', '),
          createdAt: format(workout.createdAt.toDate(), 'HH:mm dd-MM-yy'),
          updatedAt: format(workout.updatedAt.toDate(), 'HH:mm dd-MM-yy'),
        }
      }),
    [getWorkoutTypeName, workouts]
  )
  return { rows, loading, error }
}

export const useUserTableRows = () => {
  const { usersExt, loading, error } = useUsers()
  const rows = useMemo(
    () =>
      usersExt.map((user) => {
        return {
          ...user,
          credits: user.credits ?? 0,
          isCoach: user.isCoach ?? false,
          isAdmin: user.isAdmin ?? false,
        }
      }),
    [usersExt]
  )
  return { rows, loading, error }
}

interface UserTableColumnOptions {
  currentUserId?: string
  roleLoading?: string | null
  onRoleChange?: (uid: string, role: UserRole, enabled: boolean) => void
}

export const useUserTableColumns = ({
  currentUserId,
  roleLoading,
  onRoleChange,
}: UserTableColumnOptions = {}) => {
  const renderRoleSwitch = useCallback(
    (params: GridRenderCellParams, role: UserRole) => {
      const checked = Boolean(params.value)
      const uid = params.row.uid as string
      const disabled = role === 'admin' && uid === currentUserId && checked
      const loading = roleLoading === `${uid}:${role}`

      return (
        <Tooltip
          title={
            disabled
              ? 'Du kan inte ta bort din egen adminroll'
              : checked
              ? 'Ta bort roll'
              : 'Lägg till roll'
          }
        >
          <span>
            <Switch
              size="small"
              checked={checked}
              disabled={disabled || loading || !onRoleChange}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => {
                event.stopPropagation()
                onRoleChange?.(uid, role, event.target.checked)
              }}
            />
          </span>
        </Tooltip>
      )
    },
    [currentUserId, onRoleChange, roleLoading]
  )

  const columns: GridColDef[] = useMemo(() => {
    const columns: GridColDef[] = [
      { field: 'uid', headerName: 'ID', hide: true, minWidth: 100 },
      {
        field: 'displayName',
        headerName: 'Namn',
        editable: true,
        minWidth: 220,
        flex: 1,
      },
      { field: 'email', headerName: 'E-mail', minWidth: 280, flex: 1 },
      {
        field: 'phoneNumber',
        headerName: 'Telefonnummer',
        editable: true,
        minWidth: 150,
      },
      {
        field: 'isAdmin',
        headerName: 'Admin',
        type: 'boolean',
        sortable: false,
        filterable: false,
        minWidth: 90,
        renderCell: (params: GridRenderCellParams) =>
          renderRoleSwitch(params, 'admin'),
      },
      {
        field: 'isCoach',
        headerName: 'Tränare',
        type: 'boolean',
        sortable: false,
        filterable: false,
        minWidth: 90,
        renderCell: (params: GridRenderCellParams) =>
          renderRoleSwitch(params, 'coach'),
      },
      {
        field: 'credits',
        headerName: 'Credits',
        type: 'number',
        minWidth: 30,
        editable: false,
      },
      {
        field: 'photoURL',
        headerName: 'Profilbild',
        minWidth: 300,
        editable: true,
      },
    ]

    return features.credits
      ? columns
      : columns.filter((column) => column.field !== 'credits')
  }, [renderRoleSwitch])
  return columns
}
